import { useRef, useState } from 'react';
import { Button } from '@/components/ui/button.jsx';
import { toast } from '@/components/ui/toaster.jsx';
import { fileDealDocuments, announceLedgerChanged } from '@/services/deals.service.js';
import { openStoredDocument } from '@/services/docs-open.service.js';
import { formatHumanDate } from '@/lib/format.js';

// THE CARRIER'S DOCUMENT ON A LEDGER ROW (David 2026-10-29; paperwork-1 1b, L1b-B1, L1b-B2). One store: a documents row linked
// to the ledger row (linkDocumentToTransaction, the one link writer). The ledger read and the cancellation note's read hand this
// control the row's carrier document (`document`: its derived name), or null when none is linked, or `unchecked` when the read
// failed (it then says "could not check" and offers nothing, never "missing"). A filed one opens through the download door
// (GET /api/tracon/docs/stored/:documentId, logged). A missing one is filed through the deal's document door
// (fileDealDocuments: fileDocument, then the link) as the carrier's endorsement of THIS row, in one request; the success is read
// back (the stored row must name this ledger row), and "Does this replace <name>?" is asked here when the server asks it.
// Used on cancellation and endorsement rows in Transaction history and by the cancellation note.
// The words a button uses for the ledger row it acts on (the deal page is one term; the row is its type and effective date).
export function ledgerRowName(type, effectiveDate) {
  const kind = type || 'transaction';
  return effectiveDate ? `the ${kind} effective ${formatHumanDate(effectiveDate) || effectiveDate}` : `this ${kind}`;
}

export default function CarrierDocAttach({ dealId, txnId, document = null, unchecked = false, rowName = 'this transaction', onAttached, size = 'sm' }) {
  const input = useRef(null);
  const [busy, setBusy] = useState(false);
  const [filed, setFiled] = useState(null); // the stored row the door answered, read back
  const [ask, setAsk] = useState(null); // { file, candidates }: the server asked whether this replaces a filed one

  const shown = filed || document;
  if (shown) {
    return (
      <div className="mt-1.5 flex flex-wrap items-center gap-2 text-xs text-text-muted" data-carrier-doc="on-file">
        <span>Carrier's document on file for {rowName}: {shown.name}</span>
        <Button size={size} variant="ghost" onClick={() => openStoredDocument(shown.id, { notify: (w) => toast.info(w) })}>
          Open {shown.name}
        </Button>
      </div>
    );
  }
  if (unchecked) {
    return <div className="mt-1.5 text-xs text-text-muted" data-carrier-doc="unchecked">Carrier's document for {rowName}: could not check. Reload to try again.</div>;
  }

  async function attach(file, replaces) {
    if (!file) return;
    if (file.type && file.type !== 'application/pdf') { toast.error(`${file.name} is not a PDF`); return; }
    setBusy(true);
    setAsk(null);
    try {
      const body = await fileDealDocuments(dealId, [{ file, kind: 'endorsement', transaction_id: txnId, replaces_document_id: replaces }]);
      const r = body.results[0] || {};
      if (r.code === 'REPLACES_UNANSWERED') { setAsk({ file, candidates: r.candidates || [] }); return; }
      if (!r.ok) throw new Error(r.error || 'not filed');
      if (r.document?.transaction?.id !== txnId) throw new Error(`${r.document?.name || file.name} was filed but did not read back attached to ${rowName}`);
      setFiled(r.document);
      toast.success(`${file.name} filed as ${r.document.name} and attached to ${rowName}`);
      announceLedgerChanged(dealId);
      onAttached?.();
    } catch (err) {
      toast.error(`${file.name}: ${err?.body?.error || err?.message || 'failed'}`);
    } finally {
      setBusy(false);
      if (input.current) input.current.value = '';
    }
  }

  return (
    <div className="mt-1.5" data-carrier-doc="missing">
      <input ref={input} type="file" accept="application/pdf" className="hidden" onChange={(e) => attach(e.target.files?.[0])} />
      <Button size={size} variant="outline" disabled={busy} onClick={() => input.current?.click()}>
        {busy ? 'Attaching…' : `Attach carrier's document to ${rowName}`}
      </Button>
      {ask ? (
        <div className="mt-1.5 flex flex-wrap items-center gap-2 text-xs" data-carrier-doc-replace>
          <span className="text-text">Does {ask.file.name} replace {ask.candidates.map((c) => c.name).join(' or ')}?</span>
          {ask.candidates.map((c) => (
            <Button key={c.id} size={size} variant="outline" disabled={busy} onClick={() => attach(ask.file, c.id)}>Yes, it replaces {c.name}</Button>
          ))}
          <Button size={size} variant="outline" disabled={busy} onClick={() => attach(ask.file, 'none')}>No, it is another document</Button>
        </div>
      ) : null}
    </div>
  );
}
